import 'whatwg-fetch';

import { combine } from '@desktopUtils/url';
import { saveToStorage, loadFromStorage } from '@desktopUtils/storage';

import { getConfig } from './getConfigPortal';

const extend = {};

export function getProfile() {
  const profile = window.app?.profile;
  return profile;
}

function getMessage(data) {
  const { error, file, input, line } = data || {};
  let message = error || '';

  if (input && Array.isArray(input)) {
    message = 'Table names:<br/>' + input.map(d => d.table).join(', ') + '<br/><br/>' + message;
  }

  if (input && input.table) message = 'Table name: ' + input.table + '<br/><br/>' + message;

  return message;
}

function invalidateSession() {
  if (window.app) {
    window.app.invalidateSession();
  } else {
    saveToStorage('token_p', null);
  }
}

function getPortalRequestQuery(data) {
  const appPortalID = Number(window.app?.portalID);
  const dataPortalID = Number(data?.portal_id);
  return {
    portal_request: 1,
    portal_id: appPortalID > 0 ? appPortalID : (dataPortalID > 0 ? dataPortalID : 1),
  };
}

export function query(url, fetchOptions, options = {}) {
  if (window.app?.portalUnavailable) {
    return Promise.reject(new Error('Portal is temporarily unavailable.'));
  }

  if (window.app?.anonymousSessionExpired) {
    return Promise.reject(new Error('Session expired.'));
  }

  if (!options.skipIndicator) window.app?.setLoading(window.app.isLoading + 1);

  return window.fetch(url, fetchOptions)
    .then(res => {
      if (!options.skipIndicator) window.app?.setLoading(window.app.isLoading - 1);
      const contentType = res.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        // if json we continue
        return res.json();
      }
      // otherwise we just show error
      // text result came, we return promise which fails
      // with content error
      return res.text().then(text => {
        // toastr.error(text);
        throw new Error(text);
      });
    })
    .then(data => {
      // const { error } = data || {};
      // session errors are in array for some reason...
      const response = Array.isArray(data) ? data[0] : data;
      const error = response?.error || null;

      if (response?.rc === -1090) {
        window.app?.setPortalUnavailable();
        const maintenanceError = new Error(error || 'Portal is temporarily unavailable.');
        maintenanceError.portalUnavailable = true;
        throw maintenanceError;
      }

      if (url.includes('SysStats')) return;

      // we have to logon
      if ([-1000, -1001, -1002, -1010, -1081].includes(data?.rc) || [-1000, -1001, -1002, -1010, -1081].includes(data[0]?.rc)) {
        const error = data?.error ?? data[0]?.error;

        if (!options.skipError && ([-1081].includes(data?.rc) || [-1081].includes(data[0]?.rc)) && error) {
          toastr.error(error);
        }

        if (!options.ignoreInvalidSession) invalidateSession();
        throw new Error(error || 'Invalid session.');
      }

      if (error) {
        // we have to logon
        console.log('error:', error, data);
        if (error.indexOf('Please logon first') >= 0 || error.indexOf('Invalid session.') >= 0 || error.indexOf('skey') >= 0 || error.indexOf('Skey') >= 0) {
          if (!options.ignoreInvalidSession) invalidateSession();
        } else {
          data.url = url;
          data.query = fetchOptions;
          if (!options.skipLogging) {
            if (data.rc == -1) window.app.statsAddError('serverWarning', data);
            else if (data.rc == -2) window.app.statsAddError('serverWarning', data);
            else window.app.statsAddError('serverError', data);
          }
        }
        throw new Error(getMessage(data));
      }
      return data;
    })
    .catch(res => {
      if (res.portalUnavailable) throw res;

      if (!options.skipIndicator) window.app?.setLoading(window.app.isLoading - 1);
      // sometimes when JSON cant be parsed, this message appears
      // our best bet is to let you know
      if (url.includes('SysStats')) return;

      if (res.message && (res.message.match('Please logon first') || res.message.match('Invalid session.') || res.message.match('Skey is not numeric')) && window.app) {
        if (!options.ignoreInvalidSession) invalidateSession();
        console.warn(res.message);
        throw res;
      }
      if (!options.skipError) {
        if (res.message?.toLowerCase().indexOf('unexpected') >= 0) {
          toastr.error('JSON Parse: ', url);
        } else {
          toastr.error(res.message);
        }

        if (!options.skipLogging) {
          const request = { res, url, query: fetchOptions, error: res?.message };
          window.app.statsAddError('serverError', request);
        }
      }
      throw res;
    });
}

export function get(url, data = {}, options = {}) {
  let { apiUrl = '' } = getConfig();

  data = { ...data, portal_id: window.app?.portalID ?? data?.portal_id ?? null };

  if (!apiUrl?.includes?.('wr/') && !options.isGlobal) {
    apiUrl = `${apiUrl}wr/`;
  }

  apiUrl = options.isGlobal ? apiUrl.replace('wr/', '') + url : `${apiUrl}${url}`;

  if (loadFromStorage('token_p')) {
    data.skey = loadFromStorage('token_p');
  }

  // if (!options.skipProfile && !getProfile()) { return; }
  if (!options.skipProfile) {
    data.profile = getProfile(url);
  }
  if (!options.isGlobal) {
    data = { ...data, ...getPortalRequestQuery(data) };
  }
  url = combine(apiUrl, { ...data });
  return query(url, {
    method: 'GET',
    mode: 'cors',
    crossDomain: true,
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
    },
    ...extend,
  }, options);
}

export function post(url, data, options = {}) {
  let { apiUrl = '' } = getConfig();

  if (url.startsWith('/app')) {
    apiUrl = apiUrl.replace(/\/?api\/?$/, '');
  } else if (!apiUrl.includes('wr/') && !options.isGlobal) {
    apiUrl = `${apiUrl}wr/`;
  }
  apiUrl = options.isGlobal ? apiUrl.replace('wr/', '') + url : `${apiUrl}${url}`;

  const profile = getProfile(url);
  // if (!profile) { return; }
  let queryString = {};
  if (options.skipProfile) {
    queryString = options.isGlobal ? {} : getPortalRequestQuery(data);
    url = combine(apiUrl, queryString);
    console.log('url:', url);
  } else {
    queryString = options.isGlobal ? { profile } : { profile, ...getPortalRequestQuery(data) };
    if (loadFromStorage('token_p')) {
      queryString.skey = loadFromStorage('token_p');
    }
    url = combine(apiUrl, queryString);
  }

  if (data != null && typeof data === 'object' && !Array.isArray(data) && !options.text) {
    data = { ...data, portal_id: window.app?.portalID ?? data?.portal_id ?? null };
  }

  return query(url, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
    },
    // body: JSON.stringify(data),
    body: data != null && typeof(data) == "object" && !options.text ? JSON.stringify(data) : data, // do not delete .text check!! it breaks redmine attachments
    ...extend,
  }, options);
}
